import { Box, Container } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import DefaultSearchBar from "./DefaultSearchBar";
import DefaultSearchList from "./DefaultSearchList";

export type BaseSearchOption = {
  id?: string;
  name?: string;
  type?: string;
  icon?: React.ElementType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
};

type SearchDirectoryProps<SearchOption extends BaseSearchOption> = {
  WrapperComponent?: React.ElementType;
  SearchComponent?: React.ElementType;
  ListComponent?: React.ElementType;
  EmptyListComponent?: React.ElementType;
  options: SearchOption[];
  setOptions: (query: string) => Promise<void>;
  globalSelected?: SearchOption;
  setGlobalSelected?: (option: SearchOption | undefined) => void;
  disabled?: boolean;
  emptyDirectory?: boolean;
  loading?: boolean;
};

function SearchDirectory<SearchOption extends BaseSearchOption>({
  WrapperComponent,
  SearchComponent,
  ListComponent,
  EmptyListComponent,
  options,
  setOptions,
  globalSelected,
  setGlobalSelected,
  disabled,
  emptyDirectory,
  loading,
}: SearchDirectoryProps<SearchOption>) {
  const [localSelected, setLocalSelected] = useState<
    SearchOption | undefined
  >();
  const selected = globalSelected ?? localSelected;
  const setSelected = setGlobalSelected ?? setLocalSelected;
  const [query, setQuery] = useState<string>("");
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setOptions(query);
      setHasSearched(true);
    }, 250);
    return () => clearTimeout(timeout);
  }, [query, setOptions]);

  const SearchBar = SearchComponent ?? DefaultSearchBar;

  const hasOptions = options.length > 0;
  const showEmpty = !hasOptions && hasSearched;

  const listContent = useMemo(() => {
    if (hasOptions) {
      return ListComponent ? (
        <ListComponent
          options={options}
          selected={selected}
          onSelect={setSelected}
          disabled={disabled}
        />
      ) : (
        <DefaultSearchList
          options={options}
          selected={selected}
          onSelect={setSelected}
          disabled={disabled}
        />
      );
    } else if (showEmpty && EmptyListComponent) {
      return (
        <EmptyListComponent
          query={query}
          setQuery={setQuery}
          setSelected={setSelected}
          emptyDirectory={emptyDirectory}
        />
      );
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, selected, disabled, query]);

  const content = (
    <Container disableGutters>
      <SearchBar
        query={query}
        setQuery={setQuery}
        selected={selected}
        setSelected={setSelected}
        disabled={disabled}
        loading={loading}
      />
      {listContent}
    </Container>
  );

  return WrapperComponent ? (
    <WrapperComponent selected={selected}>{content}</WrapperComponent>
  ) : (
    <Box sx={{ m: 2 }}>{content}</Box>
  );
}

export default SearchDirectory;
